/**
 * DOM helpers for the MCC agent bridge. Pure enough to unit-test: card pick
 * must not use full-card innerText (other cards mention a clause number),
 * and undo must be visible from the label the lawyer sees — not only testid.
 */

export const UNDO_BUTTON_LABELS = ['בטל תגובה', 'undo response', 'מבטל', 'undoing'];
export const SUBMITTED_BADGE_LABELS = ['נשלח לתגובה', 'response submitted'];

export function foldLabel(text: string): string {
  return (text || '').replace(/\s+/g, ' ').trim().toLowerCase();
}

export function isUndoButtonLabel(text: string): boolean {
  const folded = foldLabel(text);
  return UNDO_BUTTON_LABELS.some((label) => folded.includes(label.toLowerCase()));
}

export function isSubmittedBadgeText(text: string): boolean {
  const folded = foldLabel(text);
  return SUBMITTED_BADGE_LABELS.some((label) => folded.includes(label.toLowerCase()));
}

export type ClauseCardAttrs = {
  id: string;
  number: string;
  title: string;
};

/** Exact id, then id prefix, then exact number, then title "סעיף 21.7". */
export function scoreClauseCardMatch(card: ClauseCardAttrs, needle: string): number {
  const n = (needle || '').trim();
  if (!n) return 0;
  if (card.id === n) return 400;
  if (card.id.startsWith(`${n}_`)) return 300;
  if (card.number === n) return 200;
  const title = (card.title || '').replace(/\s+/g, ' ').trim();
  if (title === n || title.endsWith(` ${n}`) || title.includes(` ${n} `)) return 100;
  return 0;
}

export function pickClauseCardIndex(cards: ClauseCardAttrs[], needle: string): number {
  let best = -1;
  let bestScore = 0;
  for (let i = 0; i < cards.length; i++) {
    const score = scoreClauseCardMatch(cards[i], needle);
    if (score > bestScore) {
      bestScore = score;
      best = i;
    }
  }
  return best;
}

export function findClauseCard(root: ParentNode, clauseNumber: string): HTMLElement | null {
  const cards = Array.from(root.querySelectorAll<HTMLElement>('[data-testid="cc-clause-card"]'));
  return chooseClauseCard(cards, clauseNumber);
}

export function findUndoButton(card: ParentNode): HTMLButtonElement | null {
  const byTestId = card.querySelector<HTMLButtonElement>('[data-testid="cc-undo-response"]');
  if (byTestId) return byTestId;
  const byClass = card.querySelector<HTMLButtonElement>('button.cc-undo-response');
  if (byClass) return byClass;
  const buttons = Array.from(card.querySelectorAll('button'));
  return buttons.find((btn) => isUndoButtonLabel(btn.textContent || '')) ?? null;
}

/** Bank-of-changes item is portaled to document.body, not inside the card. */
export function findAiSourceBocButton(root: ParentNode = document): HTMLButtonElement | null {
  return root.querySelector<HTMLButtonElement>('[data-testid="cc-ai-source-boc"]');
}

export function isSubmitToResponseLabel(text: string): boolean {
  const folded = foldLabel(text);
  return (
    folded.includes('שלח לתגובה') ||
    folded.includes('submit to response') ||
    folded.includes('מחיל') ||
    folded.includes('applying')
  );
}

/** Empty CR or "clause not found" — do not invent a QR suffix. */
export function isUnusableQrCard(card: HTMLElement): boolean {
  const number = (card.getAttribute('data-clause-number') || '').trim();
  const hasClient = card.getAttribute('data-has-client-request');
  if (/לא נמצא|not found/i.test(number)) return true;
  if (hasClient === 'false') {
    // Fully deleted client requests still render as <del> redlines — QR applies.
    const preview =
      card.querySelector('.cc-selected-text-xml') ||
      card.querySelector('[data-testid="cc-client-request-preview"]');
    const hasDeletedCr = Boolean(
      preview?.querySelector('del, .track-change-deletion, .track-change-deleted'),
    );
    if (hasDeletedCr) return false;
    return true;
  }
  return false;
}

export function findQuickResponseButton(card: ParentNode): HTMLButtonElement | null {
  const byTestId = card.querySelector<HTMLButtonElement>('[data-testid="cc-quick-response-btn"]');
  if (byTestId) return byTestId;
  const buttons = Array.from(card.querySelectorAll('button'));
  return (
    buttons.find((btn) => {
      const folded = foldLabel(btn.textContent || '');
      return folded.includes('תגובה מהירה') || folded.includes('quick response');
    }) ?? null
  );
}

/** Menu is portaled to document.body, not inside the card. */
export function findQuickResponseOption(
  root: ParentNode,
  optionId = 'see_proposed_correction',
): HTMLButtonElement | null {
  const byTestId = root.querySelector<HTMLButtonElement>(
    `[data-testid="cc-quick-response-${optionId}"]`,
  );
  if (byTestId) return byTestId;
  const needles =
    optionId === 'see_proposed_correction'
      ? ['ראה תיקון מוצע']
      : optionId === 'not_accepted'
        ? ['לא מקובל']
        : optionId === 'accepted'
          ? ['מקובל']
          : optionId === 'requires_company_approval'
            ? ['דורש אישור חברה']
            : [];
  const buttons = Array.from(root.querySelectorAll('button'));
  return (
    buttons.find((btn) => {
      const folded = (btn.textContent || '').replace(/\s+/g, ' ');
      return needles.some((needle) => folded.includes(needle));
    }) ?? null
  );
}

export function findSubmitButton(card: ParentNode): HTMLButtonElement | null {
  const byTestId = card.querySelector<HTMLButtonElement>('[data-testid="cc-submit-to-response"]');
  if (byTestId) return byTestId;
  const buttons = Array.from(card.querySelectorAll('button'));
  return buttons.find((btn) => isSubmitToResponseLabel(btn.textContent || '')) ?? null;
}

export function cardActionSignals(card: HTMLElement): Record<string, unknown> {
  const undo = findUndoButton(card);
  const submit = findSubmitButton(card);
  const labels = Array.from(card.querySelectorAll('button')).map((btn) =>
    (btn.textContent || '').replace(/\s+/g, ' ').trim(),
  );
  const submittedAttr = card.getAttribute('data-response-submitted') === 'true';
  const submittedBadge = isSubmittedBadgeText(card.innerText || card.textContent || '');
  return {
    submittedAttr,
    submittedBadge,
    hasUndo: Boolean(undo),
    undoDisabled: undo ? Boolean(undo.disabled) : null,
    undoLabel: undo ? (undo.textContent || '').replace(/\s+/g, ' ').trim() : null,
    hasSubmit: Boolean(submit),
    buttonLabels: labels.filter((label) => label.length > 0),
  };
}

/** Original CR redlines are not an AI suggestion — do not treat them as ready to apply. */
export function previewLooksLikeAiSuggestion(stats: {
  htmlHasTrackChangeNew?: unknown;
  insBlue?: unknown;
}): boolean {
  return stats.htmlHasTrackChangeNew === true || Number(stats.insBlue) > 0;
}

export function cardLooksSubmitted(card: HTMLElement): boolean {
  const signals = cardActionSignals(card);
  return Boolean(signals.submittedAttr || signals.submittedBadge || signals.hasUndo);
}

function isInViewport(el: HTMLElement): boolean {
  if (!el.isConnected) return false;
  const rect = el.getBoundingClientRect();
  if (rect.width <= 0 || rect.height <= 0) return false;
  return (
    rect.bottom > 0 &&
    rect.top < (window.innerHeight || 0) &&
    rect.right > 0 &&
    rect.left < (window.innerWidth || 0)
  );
}

/**
 * When the same clause is in the DOM twice (list + remount leftover), prefer
 * the copy the lawyer can see — especially the one with בטל תגובה.
 */
export function chooseClauseCard(cards: HTMLElement[], needle: string): HTMLElement | null {
  const attrs = cards.map((card) => ({
    id: card.getAttribute('data-clause-id') || '',
    number: card.getAttribute('data-clause-number') || '',
    title: card.querySelector('.cc-clause-title')?.textContent || '',
  }));
  let bestScore = 0;
  for (const card of attrs) {
    bestScore = Math.max(bestScore, scoreClauseCardMatch(card, needle));
  }
  if (bestScore <= 0) return null;
  const tied = cards.filter((_, i) => scoreClauseCardMatch(attrs[i], needle) === bestScore);
  const withUndo = tied.filter((card) => findUndoButton(card));
  const submitted = tied.filter((card) => cardLooksSubmitted(card));
  const pool = withUndo.length > 0 ? withUndo : submitted.length > 0 ? submitted : tied;
  const visible = pool.filter(isInViewport);
  // Last in DOM / last in view — HMR leftovers are typically earlier siblings.
  return visible[visible.length - 1] ?? pool[pool.length - 1] ?? null;
}
